import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { isCorrespondenceHiddenForCurrentUser } from '@utils/offshoreTemplateAccess'
import { Collapse } from 'antd'
import cx from 'classnames'
import { observer } from 'mobx-react'
import { useEffect, useMemo, useRef } from 'react'

import './style.scss'

/**
 * Collapse id used for the Case Correspondence section. Every module declares it through its own
 * `FormStepNames.CaseCorrespondence`, but they all resolve to this same literal — which is what
 * lets the section be hidden here, once, instead of in each of the eighteen modules that render it.
 */
const CASE_CORRESPONDENCE_ID = 'CaseCorrespondence'

const { Panel } = Collapse

const styleIcon = {
  fontSize: '16px',
}

type Props = {
  id: string
  children: JSX.Element
  isLine?: boolean
  scrollAnchor?: boolean
  className?: string
  classPanel?: string
  activeKey?: string | string[]
  disabelLine?: boolean
  header: JSX.Element | string
  sticky?: number
  onChange?: (id?: string | string[], isCloseStep?, isChangeStep?) => void
  onShowMatterDetail?: () => void
  isInvalidStep?: boolean
  forceRender?: boolean
}
const AppCollapseSingleBase = ({
  className,
  classPanel,
  header,
  activeKey = null,
  id,
  children,
  isLine = false,
  scrollAnchor = true, // allow scroll into view when it open
  sticky = undefined,
  onChange = (id, isCloseStep, isChangeStep) => {
    /* TODO document why this arrow function is empty */
  },
  onShowMatterDetail = () => {},
  isInvalidStep = false,
  forceRender = false,
}: Props) => {
  const isActive = useMemo(() => {
    if (Array.isArray(activeKey)) {
      return activeKey.includes(id)
    }
    return activeKey === id
  }, [activeKey, id])

  useEffect(() => {
    if (isActive && collapseRef && collapseRef.current) {
      if (scrollAnchor) {
        scrollToCurrentTab()
      }
    }
  }, [isActive])

  const scrollToCurrentTab = () => {
    if (collapseRef.current) {
      setTimeout(() => {
        if (collapseRef.current) {
          const fixHeaderHeight = 200
          const scrollContainer = document.getElementById('dashboard-content')
          const scrollTo =
            scrollContainer.scrollTop +
            collapseRef.current.getBoundingClientRect().top -
            fixHeaderHeight
          // collapseRef.current.scrollIntoView({ inline: 'center', behavior: 'smooth' })
          scrollContainer.scrollTo({ top: scrollTo, behavior: 'smooth' })
        }
      }, 200)
    }
  }

  const toggleCollapse = e => {
    const btn = document.getElementById('button-detail-matter')

    if (btn && btn.contains(e.target)) {
      if (typeof onShowMatterDetail === 'function') {
        onShowMatterDetail()
      }
    } else {
      const isCloseStep = isActive
      const isChangeStep = Array.isArray(activeKey)
        ? !isActive && activeKey.includes(id)
        : !isActive && !!activeKey // new update for sing step
      onChange(id, isCloseStep, isChangeStep)
    }
  }

  const collapseRef = useRef(null)

  const widthContent =
    window.innerWidth <= 1024 ? '100%' : 'calc(100% - 56.25px)'

  const enableSticky = sticky != undefined
  return (
    <div
      className={cx([className, 'mb-4'])}
      style={{ width: isLine ? widthContent : 'auto' }}
      ref={collapseRef}
      data-name={`AppCollapseSingle-${id}`}
    >
      <div
        className={cx([
          'rounded-xl mb-4 border',
          !isLine && 'bg-white pb-2',
          isLine && !isActive ? 'bg-blue-100' : 'bg-white',
          isInvalidStep && !isActive
            ? 'border-red-400 border-2'
            : 'border-grey-200',
        ])}
      >
        <Collapse ghost={true} bordered={false} activeKey={activeKey}>
          <Panel
            forceRender={forceRender}
            showArrow={false}
            className={cx(classPanel, {
              'panel-sticky': enableSticky,
              'panel-sticky__active': enableSticky && isActive,
            })}
            header={
              <div
                onClick={toggleCollapse}
                className={cx(['border-grey-200 w-full'])}
              >
                {header}
              </div>
            }
            key={id}
          >
            {children}
          </Panel>
        </Collapse>
      </div>
      <div
        style={{ zIndex: 1 }}
        className={cx([
          'w-full relative flex justify-center -mt-[28px] cursor-pointer',
          isLine && !isActive && 'hidden',
        ])}
        onClick={toggleCollapse}
      >
        <span className="bg-white w-[30px] h-[30px] hover:shadow shadow-grey-500 rounded-full flex justify-center items-center">
          {!isActive ? (
            <DownOutlined
              style={styleIcon}
              className="cursor-pointer text-blue-400"
            />
          ) : (
            <UpOutlined
              style={styleIcon}
              className="cursor-pointer text-blue-400"
            />
          )}
        </span>
      </div>
    </div>
  )
}

// observer, so the correspondence section appears as soon as the allowlist finishes loading rather
// than staying hidden from the first render.
export const AppCollapseSingle = observer(AppCollapseSingleBase)
